import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  Check,
  Copy,
  FolderOpen,
  Hourglass,
  Link2,
  Lock,
  MapPin,
  Navigation,
  Pencil,
  StickyNote,
  Trash2,
  TriangleAlert,
  UserRoundPlus,
  UserRoundX,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatDays, formatNumber, formatRelativeDays } from '@/lib/format';
import { StatusPill } from '@/components/ui/status-pill';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar } from '@/components/ui/avatar-initials';
import {
  cleanText,
  daysLate,
  describeLocation,
  endDateDays,
  matchesStatusTab,
  missingSetup,
  newProjectLabel,
  projectHref,
  safeExternalUrl,
  scopeText,
  stageSummaryOf,
  visibleTags,
} from './projectsModel';

/*
  Cells of the projects table (03 #4) and the phone cards: system chip, project name + sub line, team,
  stage with its 10-step bar (03 #6), due date and the row's ⋯ menu items.
*/

// The "E" hint only makes sense where there is a keyboard and a pointer (not on phones).
const hasKeyboardShortcuts = () => (
  typeof window === 'undefined' || typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches
);

/** Square system chip — the foundation one (components/ui/system-chip), re-exported for this family. */
export { SystemChip } from '@/components/ui/system-chip';

/** Notes as a small sticky-note icon with the text in a tooltip (hover / focus / tap) — not red text. */
export function NotesHint({ notes }) {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="relative z-[1] -m-1 grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-3 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Notes: ${notes}`}
            data-testid="project-notes-indicator"
            onClick={(event) => {
              // Tap / click toggles it (touch screens have no hover); never opens the row.
              event.preventDefault();
              event.stopPropagation();
              setOpen((value) => !value);
            }}
          >
            <StickyNote className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[320px] whitespace-pre-line bg-navy text-[12px] leading-snug text-white">
          <span className="font-semibold">Note: </span>{notes}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const CITY_SOURCE_TITLE = {
  pin: 'City from the map pin',
  location: 'City from the location',
};
const GUESSED_CITY_TITLE = 'City guessed from the project name. Confirm where the site is in Edit details.';

/** "client · place · scope" parts of the line under the name. */
export function subParts(project) {
  const client = cleanText(project.client);
  const location = describeLocation(project.location);
  // No location typed: the city the server found from the map pin or the name (03 #10).
  const city = location ? '' : cleanText(project.city);
  const scope = scopeText(project.scope);
  return [
    client && { key: 'client', node: client, text: client },
    location && {
      key: 'location',
      text: location.pin ? location.title : location.text,
      node: location.pin ? (
        <span className="inline-flex items-center gap-0.5 text-status-green-ink" title={location.title}>
          <MapPin className="h-3 w-3" aria-hidden="true" />
          {location.text}
        </span>
      ) : location.text,
    },
    // A city guessed from the name is only a hint ("Dubai?", italic): the row still says "Needs setup: city".
    city && (cleanText(project.city_source) === 'name' ? {
      key: 'location',
      text: `${city}? (guessed from the project name)`,
      node: (
        <span className="italic" title={GUESSED_CITY_TITLE} data-testid="project-city" data-guessed="true">
          {city}?<span className="sr-only"> (guessed from the project name)</span>
        </span>
      ),
    } : {
      key: 'location',
      text: city,
      node: <span title={CITY_SOURCE_TITLE[cleanText(project.city_source)] || undefined} data-testid="project-city">{city}</span>,
    }),
    scope && { key: 'scope', node: scope, text: scope },
  ].filter(Boolean);
}

export function SubLine({ parts, className }) {
  if (!parts.length) return null;
  return (
    <span className={cn('min-w-0 truncate', className)} title={parts.map((part) => part.text).join(' · ')} data-testid="project-sub">
      {parts.map((part, index) => (
        <span key={part.key}>
          {index > 0 ? <span className="text-ink-4" aria-hidden="true"> · </span> : null}
          {part.node}
        </span>
      ))}
    </span>
  );
}

/**
 * "⚠ Needs setup: client, city · Complete". `short` (the narrower table): "⚠ client, city · Complete" — the
 * words "Needs setup" stay in the tooltip and for screen readers.
 */
export function NeedsSetup({ project, onComplete, className, short = false }) {
  const missing = missingSetup(project);
  if (!missing.length) return null;
  return (
    // Never wider than the line (or the room it is given, `className`): the list of what is missing truncates
    // (full text in its tooltip), "Complete" never does.
    <span className={cn('inline-flex min-w-0 max-w-full shrink-0 items-center gap-[3px] font-medium text-status-amber-ink', className)} data-testid="project-needs-setup">
      <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate" title={`Needs setup: ${missing.join(', ')}`}>
        {short ? <span className="sr-only">Needs setup: </span> : 'Needs setup: '}
        {missing.join(', ')}
      </span>
      {onComplete ? (
        <>
          <span className="shrink-0 text-ink-4" aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => onComplete(project)}
            className="relative z-[1] shrink-0 rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Complete the setup of ${project.name}`}
          >
            Complete
          </button>
        </>
      ) : null}
    </span>
  );
}

/** Name (a link to the project), New badge, notes; under it client · place · scope, tags and Needs setup. */
export function ProjectCell({ project, onEdit, now, compact = false, note, shortSetup = false }) {
  const newLabel = newProjectLabel(project, { now });
  const notes = cleanText(project.notes);
  const tags = visibleTags(project.tags);
  const parts = subParts(project);
  const missing = missingSetup(project);
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        <Link
          to={projectHref(project)}
          className={cn(
            'min-w-0 truncate rounded-sm leading-[18px] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            compact ? 'text-[13px] font-medium text-ink-2' : 'text-[13.5px] font-semibold text-ink'
          )}
          data-testid={`project-link-${project.id}`}
          onKeyDown={(event) => {
            if ((event.key === 'e' || event.key === 'E') && !event.metaKey && !event.ctrlKey && !event.altKey && onEdit) {
              event.preventDefault();
              onEdit(project);
            }
          }}
        >
          {project.name || 'Untitled project'}
        </Link>
        {newLabel ? <StatusPill tone="lime" label={newLabel} size="sm" hideDot className="shrink-0" data-testid="project-new-badge" /> : null}
        {notes ? <NotesHint notes={notes} /> : null}
      </div>
      {note || parts.length || tags.length || missing.length ? (
        <div className={cn('mt-0.5 flex min-w-0 items-center gap-x-1.5 overflow-hidden text-ink-3', compact ? 'text-[11.5px] leading-[14px]' : 'text-[12px] leading-4')}>
          {note}
          {/* The place gives way first (its tooltip has it all) but keeps ~50px (7-8 characters): "Needs setup" takes
              at most the line minus 60px (its 4px margin, the 6px gap and that 50px). */}
          {!compact ? <SubLine parts={parts} /> : null}
          {/* Tags give way to "Needs setup" when the line is full (they stay in the edit panel). */}
          {!compact && !missing.length ? tags.map((tag) => (
            <span key={tag} className="shrink-0 rounded-[5px] border border-line bg-surface-2 px-1.5 text-[10.5px] font-semibold leading-4 text-ink-2">
              {tag}
            </span>
          )) : null}
          {!compact ? <NeedsSetup project={project} onComplete={onEdit} short={shortSetup} className={parts.length ? 'ml-1 max-w-[calc(100%-60px)]' : ''} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Project Engineer (avatar + name) with the Senior Project Engineer under it; "Assign" when nobody is set. */
export function TeamCell({ project, onAssign, compact = false }) {
  const engineer = cleanText(project.project_engineer);
  const senior = cleanText(project.senior_project_engineer);
  if (engineer || senior) {
    const name = engineer || senior;
    const sub = engineer
      ? (senior && senior !== engineer ? `SPE ${senior}` : '')
      : 'Senior Project Engineer';
    const title = [engineer && `Project Engineer: ${engineer}`, senior && `Senior Project Engineer: ${senior}`].filter(Boolean).join('\n');
    return (
      <div className="flex min-w-0 items-center gap-2" title={title} data-testid="project-team">
        <Avatar name={name} size={compact ? 22 : 26} />
        <div className="min-w-0">
          <div className={cn('truncate leading-[18px]', compact ? 'text-[12.5px] font-medium text-ink-2' : 'text-[13px] font-semibold text-ink')}>{name}</div>
          {sub && !compact ? <div className="truncate text-[12px] leading-4 text-ink-3">{sub}</div> : null}
        </div>
      </div>
    );
  }
  if (onAssign && matchesStatusTab(project, 'Active') && !compact) {
    return (
      <button
        type="button"
        onClick={() => onAssign(project)}
        className="relative z-[1] -mx-1 flex items-center gap-2 rounded-lg px-1 py-0.5 text-[13px] font-semibold text-brand-ink hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Assign a Project Engineer to ${project.name}`}
        data-testid="project-assign-engineer"
      >
        <span className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border-[1.5px] border-dashed border-[color:var(--brand-soft-border)]">
          <UserRoundPlus className="h-[13px] w-[13px]" aria-hidden="true" />
        </span>
        Assign
      </button>
    );
  }
  return <span className={cn('text-ink-3', compact ? 'text-[12.5px]' : 'text-[13px]')}>Unassigned</span>;
}

/** Group row: who is on the site's projects. */
export function GroupTeamCell({ info }) {
  if (info.unassigned === info.count) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-status-amber-ink">
        <UserRoundX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        No engineer on {info.count}
      </span>
    );
  }
  if (info.engineers.length === 1 && !info.unassigned) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <Avatar name={info.engineers[0]} size={26} />
        <span className="truncate text-[13px] font-semibold text-ink">{info.engineers[0]}</span>
      </div>
    );
  }
  return (
    <div className="min-w-0 text-[13px] leading-[18px]">
      <div className="truncate font-semibold text-ink">{info.engineers.join(', ')}</div>
      {info.unassigned ? <div className="truncate text-[12px] text-status-amber-ink">No engineer on {info.unassigned}</div> : null}
    </div>
  );
}

const STEP_CLASS = {
  done: 'bg-status-green',
  current: 'bg-brand',
  awaiting: 'bg-status-amber',
  open: 'bg-navy-200',
  rejected: 'bg-status-red',
  skipped: 'bg-line-2',
  todo: 'bg-[color:var(--status-gray-bg)]',
};
const STEP_LABEL = {
  done: 'done', current: 'current', awaiting: 'awaiting approval', open: 'in progress', rejected: 'rejected', skipped: 'not required', todo: 'not started',
};

/** The 10-segment stage bar: done green, current navy, awaiting approval amber, the rest grey. */
export function StageBar({ steps, size = 'sm', className }) {
  if (!Array.isArray(steps) || !steps.length) return null;
  const counts = steps.reduce((acc, step) => ({ ...acc, [step]: (acc[step] || 0) + 1 }), {});
  const label = Object.entries(counts).map(([step, count]) => `${count} ${STEP_LABEL[step] || step}`).join(', ');
  return (
    <span
      role="img"
      aria-label={`Stages: ${label}`}
      title={`Stages: ${label}`}
      className={cn('flex shrink-0', size === 'lg' ? 'w-full gap-[3px]' : 'gap-[2px]', className)}
      data-testid="project-stage-bar"
    >
      {steps.map((step, index) => (
        <span
          // The steps are positions in the pipeline: the index is the stage's identity here.
          key={index}
          data-step={step}
          className={cn(size === 'lg' ? 'h-1.5 flex-1 rounded-[3px]' : 'h-[5px] w-[9px] rounded-[2px]', STEP_CLASS[step] || STEP_CLASS.todo)}
        />
      ))}
    </span>
  );
}

/**
 * "Awaiting approval · 798 d" (the oldest stage waiting for the client / consultant). `short`: an hourglass
 * and "798 d" for narrow tables; the full sentence stays in the tooltip and for screen readers.
 */
export function AwaitingNote({ summary, className, hideIcon = false, short = false }) {
  if (!summary || !Number(summary.pending_approval_count)) return null;
  const days = summary.pending_approval_days;
  const hasDays = days !== null && days !== undefined;
  const count = Number(summary.pending_approval_count);
  const title = [
    `${summary.pending_approval_stage || 'A stage'} is awaiting approval`,
    summary.pending_approval_since ? `since ${formatDate(summary.pending_approval_since)}` : '',
    count > 1 ? `(${count} stages awaiting approval)` : '',
  ].filter(Boolean).join(' ');
  const text = `Awaiting approval${hasDays ? ` · ${formatDays(days)}` : ''}`;
  if (short) {
    return (
      <span className={cn('relative inline-flex min-w-0 items-center gap-0.5 whitespace-nowrap font-semibold text-status-amber-ink', className)} title={title} data-testid="project-awaiting">
        <Hourglass className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="sr-only">{text}</span>
        <span aria-hidden="true">{hasDays ? formatDays(days) : 'Awaiting'}</span>
      </span>
    );
  }
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1 whitespace-nowrap font-semibold text-status-amber-ink', className)} title={title} data-testid="project-awaiting">
      {hideIcon ? null : <Hourglass className="h-3 w-3 shrink-0" aria-hidden="true" />}
      <span className="truncate">{text}</span>
    </span>
  );
}

/**
 * Stage name + "3/10", and the step bar with the awaiting-approval note (`shortNote`: "⌛ 798 d", for the
 * narrower table).
 */
export function StageCell({ project, note, noteTitle, compact = false, awaitingFrom, shortNote = false }) {
  const summary = stageSummaryOf(project);
  // A site row shows the stage furthest behind, and the longest approval wait of any of its projects.
  const waiting = awaitingFrom ? stageSummaryOf(awaitingFrom) : summary;
  // A completed project whose stages were never closed: show where they stopped, quietly.
  const stale = Boolean(summary) && !summary.complete && matchesStatusTab(project, 'Completed');
  if (!summary) {
    return (
      <span className="relative" title="No stages">
        <span className="text-ink-4" aria-hidden="true">—</span>
        <span className="sr-only">No stages</span>
      </span>
    );
  }
  return (
    <div className="min-w-0" data-testid="project-stage">
      <div
        className={cn(
          'flex min-w-0 items-baseline gap-1 whitespace-nowrap leading-[18px]',
          compact ? 'text-[12.5px] font-medium text-ink-2' : 'text-[13px] font-semibold text-ink',
          stale && 'font-medium text-ink-3'
        )}
        title={stale ? 'The project is completed, but its stages were not all marked done' : undefined}
      >
        {summary.complete ? (
          <span className="inline-flex items-center gap-1 text-status-green-ink">
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            All stages done
          </span>
        ) : (
          <>
            <span className="truncate">{summary.current_stage}</span>
            <span className={cn('shrink-0 font-medium text-ink-3', compact ? 'text-[11.5px]' : 'text-[12px]')}>
              {summary.current_index}/{summary.count}
            </span>
          </>
        )}
        {note ? <span className="shrink-0 text-[12px] font-medium text-ink-3" title={noteTitle}>{note}</span> : null}
      </div>
      <div className={cn('flex min-w-0 items-center gap-2', compact ? 'mt-[3px]' : 'mt-[5px]')}>
        <StageBar steps={summary.steps} />
        {!compact ? <AwaitingNote summary={waiting} className="text-[11.5px] leading-4" hideIcon={!shortNote} short={shortNote} /> : null}
      </div>
    </div>
  );
}

/** End date; under it "48 d late" (red) or "92 d left" for Active projects. */
export function DueCell({ project, now, compact = false }) {
  const days = endDateDays(project, { now });
  if (days === null) {
    return (
      <span className="relative" title="No end date">
        <span className="text-ink-4" aria-hidden="true">—</span>
        <span className="sr-only">No end date</span>
      </span>
    );
  }
  const late = daysLate(project, { now });
  const active = matchesStatusTab(project, 'Active');
  return (
    <div className="min-w-0 whitespace-nowrap" data-testid="project-due">
      <div className={cn('leading-[18px]', compact ? 'text-[12.5px] text-ink-2' : 'text-[13.5px] font-semibold text-ink')} title={formatDate(project.end_date)}>
        {/* This year's dates without the year ("13 Aug"); other years keep it. */}
        {formatDate(project.end_date, { year: 'auto' })}
      </div>
      {active ? (
        <div className={cn('text-[12px] leading-4', late ? 'font-semibold text-status-red-ink' : 'text-ink-3')}>
          {formatRelativeDays(project.end_date, { style: 'due', now })}
        </div>
      ) : null}
    </div>
  );
}

/** Phone card: the due part of the bottom row ("48 d late" red with a calendar icon, else muted). */
export function DueBadge({ project, now }) {
  const days = endDateDays(project, { now });
  if (days === null) return <span className="text-[12.5px] text-ink-3">No end date</span>;
  const late = daysLate(project, { now });
  if (late) {
    return (
      <span className="inline-flex items-center gap-1 text-[12.5px] font-bold text-status-red-ink">
        <CalendarClock className="h-[13px] w-[13px]" aria-hidden="true" />
        {formatNumber(late, { maxDecimals: 0 })} d late
      </span>
    );
  }
  return <span className="text-[12.5px] text-ink-3">{matchesStatusTab(project, 'Active') ? formatRelativeDays(project.end_date, { style: 'due', now }) : formatDate(project.end_date)}</span>;
}

/**
 * The row's ⋯ menu: Open, Edit details (E), Duplicate for another system, Open in Waze, Copy link and —
 * Super Admin only (owner decision, enforced by the server) — Delete project….
 */
export function projectMenuItems(project, { navigate, canEdit, canDelete, onEdit, onDuplicate, onDelete, onCopyLink }) {
  const wazeUrl = safeExternalUrl(project.waze_url);
  return [
    { label: 'Open project', icon: FolderOpen, onSelect: () => navigate(projectHref(project)), testId: 'project-action-open' },
    canEdit && {
      label: 'Edit details', icon: Pencil, shortcut: hasKeyboardShortcuts() ? 'E' : undefined, onSelect: () => onEdit(project), testId: 'project-action-edit',
    },
    canEdit && { label: 'Duplicate for another system', icon: Copy, onSelect: () => onDuplicate(project), testId: 'project-action-duplicate' },
    {
      label: 'Open in Waze',
      icon: Navigation,
      disabled: !wazeUrl,
      hint: wazeUrl ? undefined : 'No link',
      onSelect: () => window.open(wazeUrl, '_blank', 'noopener,noreferrer'),
      testId: 'project-action-waze',
    },
    { label: 'Copy link', icon: Link2, onSelect: () => onCopyLink(project), testId: 'project-action-copy-link' },
    canDelete && {
      label: 'Delete project…',
      icon: Trash2,
      danger: true,
      hint: (
        <span className="inline-flex items-center gap-1 font-normal">
          <Lock className="h-3 w-3" aria-hidden="true" />
          Super Admin
        </span>
      ),
      onSelect: () => onDelete(project),
      testId: 'project-action-delete',
    },
  ];
}
